import React from "react";
import { Modal, Table, Typography } from "antd";
import type { TableColumnsType } from "antd";
import { useTranslation } from "react-i18next";

interface KeyboardShortcutsModalProps {
    open: boolean;
    onClose: () => void;
}

interface ShortcutRow {
    key: string;
    keys: string[];
    action: string;
    where: string;
}

// Keep in sync with the keydown handlers in the *Playground components,
// useProblemStatement (F4, Shift+F4) and ProblemStatementModal (F9).
const SHORTCUTS: { keys: string[]; id: string }[] = [
    { keys: ["F5"], id: "runF5" },
    { keys: ["Ctrl", "Enter"], id: "runCtrlEnter" },
    { keys: ["Ctrl", "S"], id: "save" },
    { keys: ["F4"], id: "toggleStatement" },
    { keys: ["Shift", "F4"], id: "dockStatement" },
    { keys: ["F9"], id: "fullscreenStatement" },
];

export default function KeyboardShortcutsModal({
    open,
    onClose,
}: KeyboardShortcutsModalProps) {
    const { t } = useTranslation();

    const rows: ShortcutRow[] = SHORTCUTS.map(({ keys, id }) => ({
        key: id,
        keys,
        action: t(`shortcuts.${id}.action`),
        where: t(`shortcuts.${id}.where`),
    }));

    const columns: TableColumnsType<ShortcutRow> = [
        {
            title: t("shortcuts.column.keys"),
            dataIndex: "keys",
            width: 140,
            render: (keys: string[]) => (
                <span style={{ whiteSpace: "nowrap" }}>
                    {keys.map((k, i) => (
                        <React.Fragment key={k}>
                            {i > 0 && " + "}
                            <Typography.Text keyboard>{k}</Typography.Text>
                        </React.Fragment>
                    ))}
                </span>
            ),
        },
        { title: t("shortcuts.column.action"), dataIndex: "action" },
        { title: t("shortcuts.column.where"), dataIndex: "where" },
    ];

    return (
        <Modal
            title={t("shortcuts.title")}
            open={open}
            onCancel={onClose}
            footer={null}
            width={720}
        >
            <Table<ShortcutRow>
                dataSource={rows}
                columns={columns}
                pagination={false}
                size="small"
            />
            <Typography.Paragraph type="secondary" style={{ marginTop: 12 }}>
                {t("shortcuts.statementNote")}
            </Typography.Paragraph>
        </Modal>
    );
}
